import cn from 'classnames';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { MdDoubleArrow } from 'react-icons/md';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Area, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';
import { Payload as TooltipPayload } from 'recharts/types/component/DefaultTooltipContent';
import {
  BenchmarkingQueryMetaData,
  getBenchmarkingDataColsWithSpecialCases as getBenchmarkingDataCols,
  Tercile
} from '@blisspointmedia/bpm-types/dist/Benchmarking';
import { colOrMetricRequiresKpi } from '@blisspointmedia/bpm-types/dist/Columns';
import { Formatter, toPrettyPercent } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { App2Channels } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { ChartContainer } from '@Components/ChartContainer';
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE
} from '@Components/Charts/ChartConstants';
import { ChartTooltip, PayloadItem } from '@Components/Charts/ChartTooltip/ChartTooltip';
import { useDynamicYAxisWidth } from '@Components/Charts/ChartUtils';
import { SimpleLegend } from '@Components/Charts/Legend/SimpleLegend';
import { MetricDropdown } from '@Components/MetricDropdown/MetricDropdown';
import { dateToMddyy, formatDateLabel } from '../../utils/date-utils';
import { exportToExcel } from '../../utils/download-utils';
import { snakeCaseToCapitalized } from '../../utils/data';
import { BenchmarkingChartTableView } from './BenchmarkingChartTableView';
import {
  COLOR_MAP,
  formatPercent,
  makeKpiColNameFromAlias,
  NO_SPEND_TERCILE_CHANNELS,
  sortPayloadByCompanyThenValue,
  TERCILE_DATA_PRETTY_NAME_MAP,
  TERCILE_PRETTY_NAME_MAP,
  TransformedData,
  TransformedDatum
} from './benchmarkingConstants';

export type { TransformedData, TransformedDatum };

interface ChannelBenchmarkingChartProps {
  companyPrettyName: string;
  channel: App2Channels;
  isGraphView: boolean;
  companyName: string;
  activeMetaData: BenchmarkingQueryMetaData;
  isPrimary: boolean;
  chartData: TransformedData;
  showPercentChange: boolean;
  tercileOptions: string[];
  selectedDatePeriod: string;
  kpis?: string[] | null;
}

export const ChannelBenchmarkingChart = ({
  companyPrettyName,
  channel,
  isGraphView,
  companyName,
  activeMetaData,
  isPrimary,
  chartData,
  showPercentChange,
  tercileOptions,
  selectedDatePeriod,
  kpis
}: ChannelBenchmarkingChartProps) => {
  const [hoveredLegend, setHoveredLegend] = useState<string | null>(null);
  const [activeTercileLine, setActiveTercileLine] = useState<string | null>(null);
  const [metricOptionsConfigMap, metricDropDownOptions] = useMemo(() => {
    const fullOptionsConfig = getBenchmarkingDataCols(channel);
    const configMap = fullOptionsConfig.reduce(
      (acc, m) => {
        acc[m.name] = m;
        return acc;
      },
      {} as Record<string, (typeof fullOptionsConfig)[number]>
    );
    const dropdownOptions = fullOptionsConfig.map(m => ({
      value: m.name,
      label: m.prettyName
    }));

    return [configMap, dropdownOptions];
  }, [channel]);
  const initialMetricIndex = isPrimary || metricDropDownOptions.length === 1 ? 0 : 1;
  const [selectedMetricRaw, setSelectedMetric] = useState(
    metricDropDownOptions[initialMetricIndex].value
  );
  const [selectedKpi, setSelectedKpi] = useState(
    colOrMetricRequiresKpi(selectedMetricRaw) ? (kpis && kpis[0]) || null : null
  );
  const selectedMetric = useMemo(
    () =>
      selectedKpi ? makeKpiColNameFromAlias(selectedKpi, selectedMetricRaw) : selectedMetricRaw,
    [selectedKpi, selectedMetricRaw]
  );
  const { xAxisHeight, yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    reCalcDepList: [selectedMetric, showPercentChange, activeTercileLine, chartData]
  });

  // setState during render: reset selected metric/kpi when the available options change,
  // since the previous selection may not be available in the new channel
  const [prevMetricDeps, setPrevMetricDeps] = useState({ metricDropDownOptions, isPrimary, kpis });
  if (
    prevMetricDeps.metricDropDownOptions !== metricDropDownOptions ||
    prevMetricDeps.isPrimary !== isPrimary ||
    prevMetricDeps.kpis !== kpis
  ) {
    setPrevMetricDeps({ metricDropDownOptions, isPrimary, kpis });
    const metricIndex = isPrimary || metricDropDownOptions.length === 1 ? 0 : 1;
    const metric = metricDropDownOptions[metricIndex].value;
    const hasKpi = Boolean(kpis && kpis.length);
    let kpi: string | null = null;
    if (colOrMetricRequiresKpi(metric) && hasKpi && kpis) {
      [kpi] = kpis;
    }
    setSelectedMetric(metric);
    setSelectedKpi(kpi);
  }

  const [metricFormatter, higherIsBetter, selectedMetricPrettyNameRaw] = useMemo(() => {
    const activeConfig = metricOptionsConfigMap[selectedMetricRaw];
    const formatter = activeConfig?.formatter;
    const prettyName = activeConfig?.prettyName;
    const isHigherValueBetter =
      activeConfig && 'isHigherValueBetter' in activeConfig
        ? activeConfig.isHigherValueBetter
        : undefined;
    const safeFormatter: Formatter = formatter || (val => String(val));
    return [safeFormatter, isHigherValueBetter, prettyName];
  }, [metricOptionsConfigMap, selectedMetricRaw]);

  const selectedMetricPrettyName = useMemo(
    () =>
      selectedKpi
        ? `${selectedMetricPrettyNameRaw}, ${snakeCaseToCapitalized(selectedKpi.replace(/-/g, '_'))}`
        : selectedMetricPrettyNameRaw,
    [selectedKpi, selectedMetricPrettyNameRaw]
  );

  const [companyTercile, renderTercileMap] = useMemo(() => {
    const renderMap: Partial<Record<Tercile, boolean>> = {};
    for (const tercile of Object.keys(activeMetaData.sample_sizes)) {
      const render = activeMetaData.sample_sizes[tercile] > 9;
      renderMap[tercile] = render;
    }
    return [activeMetaData.company_tercile, renderMap];
  }, [activeMetaData]);

  // setState during render: sync activeTercileLine to the company tercile when activeMetaData changes
  const [prevMetaData, setPrevMetaData] = useState<BenchmarkingQueryMetaData | null>(null);
  if (prevMetaData !== activeMetaData) {
    setPrevMetaData(activeMetaData);
    if (renderTercileMap[activeMetaData.company_tercile]) {
      setActiveTercileLine(activeMetaData.company_tercile);
    }
  }

  const tercilePrettyNameMap = useMemo(() => {
    const mapCopy = { ...TERCILE_PRETTY_NAME_MAP };
    mapCopy[companyTercile] = `${mapCopy[companyTercile]} *`;
    if (NO_SPEND_TERCILE_CHANNELS.has(channel)) {
      mapCopy.tercile_1 = 'All Spenders *';
    }
    return mapCopy;
  }, [companyTercile, channel]);

  const chartItems = useMemo(() => {
    const lines = tercileOptions.map((tercile, index) => {
      const isGeneralView = activeTercileLine === null;
      const tercileLine = renderTercileMap[tercile] ? (
        <Line
          dot={false}
          dataKey={`${tercile}_median_${selectedMetric}`}
          isAnimationActive={false}
          key={`tercile-line-${index}`}
          name={tercilePrettyNameMap[tercile]}
          stroke={COLOR_MAP[tercile]}
          strokeWidth={2}
          type='monotone'
        />
      ) : null;

      return isGeneralView || (showPercentChange && tercile === activeTercileLine) ? (
        <Fragment key={`fragmentLine${tercile}`}>{tercileLine}</Fragment>
      ) : (
        <Fragment key={`line-${index}`}>
          {tercile === activeTercileLine && (
            <>
              <Fragment key={`tercileRange-${index}`}>
                <Area
                  type='linear'
                  dataKey={`${tercile}_lower_${selectedMetric}`}
                  fillOpacity={0}
                  strokeWidth={0}
                  stroke='transparent'
                  dot={false}
                  activeDot={false}
                  stackId='tercileRange'
                  key={`lower-${index}`}
                  name={`${tercilePrettyNameMap[tercile]} ${TERCILE_DATA_PRETTY_NAME_MAP.lower}`}
                />
                <Area
                  type='linear'
                  dataKey={`${tercile}_upper_${selectedMetric}`}
                  fill={COLOR_MAP[tercile]}
                  stroke='transparent'
                  fillOpacity={0.25}
                  strokeWidth={0}
                  activeDot={false}
                  dot={false}
                  stackId='tercileRange'
                  key={`band-${index}`}
                  name={`${tercilePrettyNameMap[tercile]} ${TERCILE_DATA_PRETTY_NAME_MAP.upper}`}
                />
              </Fragment>
              <Line
                dataKey={`${tercile}_median_${selectedMetric}`}
                dot={false}
                isAnimationActive={false}
                key={`line-element-${index}`}
                stroke={COLOR_MAP[tercile]}
                strokeWidth={2}
                type='monotone'
                name={`${tercilePrettyNameMap[tercile]} ${TERCILE_DATA_PRETTY_NAME_MAP.median}`}
              />
            </>
          )}
        </Fragment>
      );
    });
    const companyLine = tercileOptions.map((tercile, index) => (
      <Line
        dot={false}
        dataKey={`${tercile}_company_${selectedMetric}`}
        stroke={COLOR_MAP.company}
        fillOpacity={1}
        isAnimationActive={false}
        key={`company-line-${index}`}
        name={companyName}
        strokeWidth={4}
        type='monotone'
      />
    ));
    return [...lines, ...companyLine];
  }, [
    activeTercileLine,
    tercileOptions,
    selectedMetric,
    companyName,
    tercilePrettyNameMap,
    renderTercileMap,
    showPercentChange
  ]);

  const yAxis = (
    <YAxis
      axisLine={false}
      domain={['auto', 'auto']}
      orientation='left'
      tick={TICK_STYLE}
      tickFormatter={val => (showPercentChange ? formatPercent(val) : metricFormatter(val))}
      tickLine={false}
      type='number'
      width={yAxisWidth}
    />
  );
  const xAxis = (
    <XAxis
      axisLine={false}
      dataKey='date'
      height={xAxisHeight}
      tick={TICK_STYLE}
      tickFormatter={val => dateToMddyy(val)}
    />
  );

  const excelDownload = useCallback(() => {
    const filteredData = chartData.map(row => {
      const newRow: TransformedDatum = { date: row.date };
      for (const dim of Object.keys(row)) {
        if (row[dim] !== null) {
          newRow[dim] = row[dim];
        }
      }
      return newRow;
    });
    exportToExcel(filteredData, `${channel}_${selectedMetric}_benchmarking_data`);
  }, [chartData, channel, selectedMetric]);

  const tooltip = (
    <Tooltip
      content={
        <BenchmarkingTooltipContent
          activeTercileLine={activeTercileLine}
          showPercentChange={showPercentChange}
          metricFormatter={metricFormatter}
          companyName={companyName}
          tercilePrettyNameMap={tercilePrettyNameMap}
          higherIsBetter={higherIsBetter}
        />
      }
      isAnimationActive={false}
    />
  );

  const handleLegendClick = (val: string) => {
    // don't allow the company line to become active on its own
    setActiveTercileLine(a => {
      if (val === 'company') return a;
      if (a === val) return null;
      return val;
    });
  };

  return (
    <ChartContainer
      className={cn('channel-benchmarking-chart-container', {
        primary: isPrimary
      })}
      title={
        <div>
          <MetricDropdown
            metrics={metricDropDownOptions}
            onChange={m => {
              setSelectedMetric(m.metric);
              setSelectedKpi(m.kpi);
            }}
            value={{ metric: selectedMetricRaw, source: null, kpi: selectedKpi }}
            kpis={
              kpis?.map(kpi => ({
                value: kpi,
                label: snakeCaseToCapitalized(kpi.replace(/-/g, '_'))
              })) || []
            }
            showPrettyKpiLabel
            closeOnScroll
            showSearchBar={false}
          />
        </div>
      }
      rightActions={<DownloadDropdown onClickOptions={[excelDownload]} menuOptions={['XLSX']} />}
      footerContent={
        isGraphView ? undefined : (
          <span className='includes-company-info-line'>{`*Includes ${companyName}`}</span>
        )
      }
    >
      {isGraphView ? (
        <div className='channel-benchmarking-chart-content'>
          <div className='chart-graphics'>
            <AutoSizer
              key='channelBenchmarkingAutoSizer'
              renderProp={({ width = 0, height = 0 }) => (
                <ComposedChart data={chartData} height={height} width={width} ref={setChartRef}>
                  <CartesianGrid
                    stroke={CARTESIAN_GRID_STROKE}
                    strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                    vertical={false}
                  />
                  {xAxis}
                  {yAxis}
                  {chartItems}
                  {tooltip}
                </ComposedChart>
              )}
            />
          </div>
          <div className='chart-legend-container'>
            <SimpleLegend
              legendItems={[
                { label: companyPrettyName, value: 'company', alwaysActive: true },
                ...tercileOptions
                  .sort()
                  .filter(t => renderTercileMap[t])
                  .map(t => ({ label: tercilePrettyNameMap[t], value: t }))
              ]}
              activeItem={activeTercileLine}
              handleLegendClick={handleLegendClick}
              setHoveredLegend={setHoveredLegend}
              handleLegendEnter={() => {}}
              hoveredLegend={hoveredLegend}
              colorMap={COLOR_MAP}
              showDash
            />
            <span className='includes-company-info-line'>{`*Includes ${companyName}`}</span>
          </div>
        </div>
      ) : (
        <BenchmarkingChartTableView
          data={chartData}
          selectedMetric={selectedMetric}
          selectedMetricPrettyName={selectedMetricPrettyName}
          tercileOptions={tercileOptions}
          activeFormatter={metricFormatter}
          companyName={companyName}
          tercilePrettyNameMap={tercilePrettyNameMap}
          companyTercile={companyTercile}
          renderTercileMap={renderTercileMap}
          showPercentChange={showPercentChange}
          selectedDatePeriod={selectedDatePeriod}
        />
      )}
    </ChartContainer>
  );
};

interface BenchmarkingTooltipContentProps {
  active?: boolean;
  label?: string | number;
  payload?: readonly TooltipPayload[];
  activeTercileLine: string | null;
  showPercentChange: boolean;
  metricFormatter: Formatter;
  companyName: string;
  tercilePrettyNameMap: Record<string, string>;
  higherIsBetter: boolean | undefined;
}

const BenchmarkingTooltipContent = ({
  active,
  label,
  payload,
  activeTercileLine,
  showPercentChange,
  metricFormatter,
  companyName,
  tercilePrettyNameMap,
  higherIsBetter
}: BenchmarkingTooltipContentProps) => {
  if (!active || !payload || !payload[0]) return null;

  let formattedHeaderLabel = formatDateLabel(String(label), {}, undefined, true);
  // general view
  if (activeTercileLine === null || showPercentChange) {
    const items = [...payload].sort(sortPayloadByCompanyThenValue).map(item => ({
      label: item.name,
      value: showPercentChange
        ? toPrettyPercent(item.value as number)
        : metricFormatter(item.value as number),
      color: item.stroke
    }));
    if (showPercentChange && payload[0].payload && payload[0].payload.compDate) {
      const { compDate } = payload[0].payload;
      formattedHeaderLabel = `${formattedHeaderLabel} vs ${formatDateLabel(
        compDate,
        {},
        undefined,
        true
      )}`;
    }

    return (
      <ChartTooltip
        headerLabel={formattedHeaderLabel}
        items={items}
        shape='line'
        colonSeparator={false}
        className='x-client-benchmarking-chart-tooltip'
      />
    );
  }

  const sortedPayload = [...payload].sort(sortPayloadByCompanyThenValue);
  const upperItem = sortedPayload.find(item => (item.dataKey as string).includes('upper_'));
  const lowerItem = sortedPayload.find(item => (item.dataKey as string).includes('lower_'));
  const lineItems = sortedPayload.filter(
    item =>
      !(item.dataKey as string).includes('upper_') && !(item.dataKey as string).includes('lower_')
  );
  const companyItem = lineItems.find(item => item.name === companyName);
  const medianItem = lineItems.find(item => item.name !== companyName);

  const confidenceLegendItemInfo = {
    label: 'CI',
    value: '',
    lowerValue: lowerItem ? metricFormatter(lowerItem.value as number) : '',
    upperValue: upperItem ? metricFormatter(upperItem.value as number) : '',
    color: upperItem?.fill || ''
  };

  const items: PayloadItem[] = lineItems.map(item => ({
    label: item.name,
    value: metricFormatter(item.value as number),
    color: item.stroke
  }));

  const companyVal = companyItem?.value as number | undefined;
  const medianVal = medianItem?.value as number | undefined;
  const medianCompGroup = medianItem?.name?.toString().replace('Median', '');
  const percentDiff = companyVal && medianVal ? (companyVal - medianVal) / medianVal : undefined;
  const isPositive = (percentDiff ?? 0) > 0;
  const isBetterValue = isPositive ? higherIsBetter : !higherIsBetter;

  const percentDiffLabel = toPrettyPercent(Math.abs(percentDiff || 0));

  items.push({
    label: `${tercilePrettyNameMap[activeTercileLine]} CI`,
    value: `${confidenceLegendItemInfo.lowerValue} – ${confidenceLegendItemInfo.upperValue}`,
    color: confidenceLegendItemInfo.color,
    shape: 'square',
    iconClassName: 'matchSquareToLinePadding partialOpacity'
  });

  const validDiff =
    percentDiff !== undefined && !Number.isNaN(percentDiff) && Math.abs(percentDiff) !== Infinity;

  const percentIndicator = validDiff && (
    <div
      className={cn('percent-indicator', {
        'worse-outcome': !isBetterValue
      })}
    >
      <span>{`${percentDiffLabel} `}</span>
      <MdDoubleArrow
        className={cn('arrow', {
          negative: !isPositive
        })}
      />
      <span>{medianCompGroup}</span>
    </div>
  );

  return (
    <ChartTooltip
      headerLabel={
        <div className='x-client-benchmarking-tooltip-header-label'>
          <span>{formattedHeaderLabel}</span>
          {percentIndicator}
        </div>
      }
      items={items}
      shape='line'
      colonSeparator={false}
      className='x-client-benchmarking-chart-tooltip'
    />
  );
};
